import {
  ToolkitFeatureFlag,
  PopoverProvider,
  PopoverTrigger,
  TabProvider,
  TabList,
  Tab,
  TabPanel,
} from '@cko/primitives';
import React, { useState } from 'react';
import { ErrorBoundary } from '@cko/dashboard-shared';
import { useDeveloperToolsProvider } from '../../context';
import { OverrideKeys } from '../../types';
import { useDeveloperToolsEnabled } from '../../useDeveloperToolsEnabled';
import { DeveloperToolsIcon } from '../DeveloperToolsIcon';
import { OverlayToolbar } from './OverlayToolbar';
import { FeatureFlagsPanel, MfeEntriesPanel, PermissionsPanel } from './Panels';
import {
  PopoverTriggerContainer,
  StyledPopover,
  StyledStatusDot,
  StyledPopoverContainer,
  StyledSecondaryButton,
  StyledBadge,
} from './OverlayPanel.style';

export const OverlayPanel = () => {
  const isDeveloperToolsEnabled = useDeveloperToolsEnabled();
  const { selectedTab, setSelectedTab, overrides } = useDeveloperToolsProvider();
  const [open, setOpen] = useState<boolean>(false);

  const setTab = (tab: string) => {
    if (Object.keys(overrides).includes(tab)) {
      setSelectedTab(tab as OverrideKeys);
    }
  };

  const close = () => setOpen(!open);
  const permissionsCount = Object.keys(overrides.permissions).length;
  const featureFlagsCount = Object.keys(overrides.featureFlags).length;
  const mfeEntriesCount = Object.keys(overrides.mfeEntries).length;

  const badgeCount = permissionsCount + featureFlagsCount + mfeEntriesCount;

  if (!isDeveloperToolsEnabled) {
    return null;
  }

  return (
    <ToolkitFeatureFlag flag="v11">
      <PopoverProvider open={open} onOpenChange={close}>
        <PopoverTriggerContainer>
          <PopoverTrigger>
            <StyledSecondaryButton onClick={close} hasOverrides={badgeCount > 0}>
              <DeveloperToolsIcon />
              {badgeCount > 0 && <StyledBadge>{badgeCount}</StyledBadge>}
            </StyledSecondaryButton>
          </PopoverTrigger>
        </PopoverTriggerContainer>
        <StyledPopover defaultDirection="bottom">
          <StyledPopoverContainer>
            <ErrorBoundary>
              <div style={{ flex: '1 1 auto', width: '100%', height: '100%' }}>
                <TabProvider currentValue={selectedTab} onChange={setTab}>
                  <TabList aria-label="dev tools tab list">
                    <Tab size="sm" value="permissions">
                      {permissionsCount > 0 && (
                        <StyledStatusDot aria-label="Permissions overridden" />
                      )}
                      Permissions
                    </Tab>
                    <Tab size="sm" value="featureFlags">
                      {featureFlagsCount > 0 && (
                        <StyledStatusDot aria-label="Feature flags overridden" />
                      )}
                      Feature Flags
                    </Tab>
                    <Tab size="sm" value="mfeEntries">
                      {mfeEntriesCount > 0 && (
                        <StyledStatusDot aria-label="MFE entries overridden" />
                      )}
                      MFE Entries
                    </Tab>
                  </TabList>
                  <TabPanel value="permissions">
                    <PermissionsPanel />
                  </TabPanel>
                  <TabPanel value="featureFlags">
                    <FeatureFlagsPanel />
                  </TabPanel>
                  <TabPanel value="mfeEntries">
                    <MfeEntriesPanel />
                  </TabPanel>
                </TabProvider>
              </div>
            </ErrorBoundary>
            <OverlayToolbar />
          </StyledPopoverContainer>
        </StyledPopover>
      </PopoverProvider>
    </ToolkitFeatureFlag>
  );
};
